import React, { useMemo } from 'react';
import { uniqueId } from 'lodash';
import classNames from 'classnames';

import { withParentSize } from '@visx/responsive';
import { GridPolar } from '@visx/grid';
import CircleChartWedge from './CircleChartWedge';
import { scaleLinear } from '@visx/scale';
import { useCallback } from 'react';
import {
  categoryRingSpans,
  radialPieSeparators,
  radialPieWedges,
} from './radialPieWedges';

const CircleChart = ({
  parentWidth,
  margin,
  questionSets,
  categoryColorMap,
  onSelectSlice = () => {},
  onClearSlices = () => {},
}) => {
  const [outerRingMaskId, unansweredMaskId] = useMemo(() => {
    const chartId = uniqueId('circle-chart-');
    return [`${chartId}-outer-ring-mask`, `${chartId}-unanswered-mask`];
  }, []);

  const handleClearSlices = useCallback(() => onClearSlices(), [onClearSlices]);

  const margins = { top: 0, right: 0, bottom: 0, left: 0, ...margin };

  const chartWidth = parentWidth;
  const width = parentWidth - (margins.left + margins.right);
  const height = width;
  const chartHeight = height + margins.top + margins.bottom;

  const { flattenToQuestions, wedges } = useMemo(
    () => radialPieWedges(questionSets),
    [questionSets]
  );

  const wedgeArc = (2 * Math.PI) / (wedges.length || 1);
  const categorySpans = useMemo(() => categoryRingSpans(wedges), [wedges]);

  const rMax = width / 2 - 5;
  const rInner = rMax - 15;
  const center = { x: width / 2, y: width / 2 };
  const radialScale = scaleLinear({ domain: [0, 1], range: [0, rInner] });
  const separators = radialPieSeparators({
    wedges,
    flattenToQuestions,
    rMax,
    rInner,
  });

  return (
    <svg
      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
      className="assessment-history-circle-chart"
    >
      <mask id={outerRingMaskId}>
        <circle
          cx={width / 2}
          cy={width / 2}
          r={rMax}
          fill="#fff"
          className="circle-chart-outer-mask"
        />
        <circle
          cx={width / 2}
          cy={width / 2}
          r={rInner}
          fill="#000"
          className="circle-chart-outer-mask"
        />
      </mask>
      <mask id={unansweredMaskId}>
        <GridPolar
          left={width / 2}
          top={width / 2}
          outerRadius={rInner}
          innerRadius={0}
          scaleAngle={radialScale}
          scaleRadial={radialScale}
          numTicksRadial={20}
          numTicksAngle={0}
          strokeWidthRadial={rInner / 40}
        />
      </mask>

      <g transform={`translate(${margins.left},${margins.right})`}>
        <g className="wedges">
          {categorySpans.map((span) => (
            <g
              className={categoryColorMap[span.category]}
              key={span.category ?? 'uncategorized'}
            >
              <CircleChartWedge
                className="chart-wedge-category"
                x={width / 2}
                y={width / 2}
                r={rMax}
                theta={wedgeArc * span.count}
                thetaOffset={-Math.PI / 2 + wedgeArc * span.start}
                mask={`url(#${outerRingMaskId})`}
              />
            </g>
          ))}
        </g>
        <g className="wedges" onMouseLeave={handleClearSlices}>
          {wedges.map((wedge, index) => (
            <g className={categoryColorMap[wedge.category]} key={wedge.key}>
              <g
                className="circle-chart-wedge-hover-target"
                onMouseEnter={() => onSelectSlice(wedge)}
              >
                <CircleChartWedge
                  className="chart-wedge-hover-spacer"
                  x={width / 2}
                  y={width / 2}
                  r={rInner}
                  theta={wedgeArc}
                  thetaOffset={-Math.PI / 2 + wedgeArc * index}
                />
                <CircleChartWedge
                  className={classNames({
                    'chart-wedge-question-set': true,
                    unanswered: wedge.normalizedScore === null,
                  })}
                  x={width / 2}
                  y={width / 2}
                  r={(wedge.normalizedScore ?? 1) * rInner}
                  theta={wedgeArc}
                  thetaOffset={-Math.PI / 2 + wedgeArc * index}
                  mask={
                    wedge.normalizedScore === null
                      ? `url(#${unansweredMaskId})`
                      : null
                  }
                />
              </g>
            </g>
          ))}
        </g>
        <g style={{ pointerEvents: 'none' }}>
          {separators.map((separator) => {
            const thetaOffset = -Math.PI / 2 + wedgeArc * separator.index;
            return (
              <line
                key={`separator-${separator.index}`}
                x1={center.x}
                y1={center.y}
                x2={center.x + separator.radius * Math.cos(thetaOffset)}
                y2={center.y + separator.radius * Math.sin(thetaOffset)}
                className={separator.className}
              />
            );
          })}
          {[0.2, 0.4, 0.6, 0.8].map((scale) => (
            <circle
              key={scale}
              r={scale * rInner}
              cx={width / 2}
              cy={width / 2}
              className="chart-grid-dense"
            />
          ))}
        </g>
      </g>
    </svg>
  );
};

export default withParentSize(CircleChart);
